import _ from 'lodash';

import type LigneType from '@/types/ligne';
import type LigneRecuType from '@/types/ligneRecu';

/**
 * Regroupe les lignes de facture par libellé de produit.
 * Pour chaque groupe : un seul représentant avec montant/reste/quantité cumulés.
 * Retourne aussi un mapping { identifiantOriginal → identifiantReprésentatif }
 */
export const grouperLignesParProduit = (
  lignes: LigneType[],
): { lignesGroupees: LigneType[]; mapping: Record<number, number> } => {
  const grouped = _.groupBy(lignes, (ligne) => ligne?.produit?.libelle || '');
  const lignesGroupees: LigneType[] = [];
  const mapping: Record<number, number> = {};

  _.forEach(grouped, (groupeLignes) => {
    // La quantité n'existe que pour les lignes issues d'un encaissement au comptant : on ne la
    // cumule que si au moins une ligne du groupe la porte, pour ne pas transformer une absence
    // de quantité en 0.
    const quantiteRenseignee = _.some(groupeLignes, (l) => !_.isNil(l.quantite));

    const representative: LigneType = {
      ...groupeLignes[0],
      montant: _.sumBy(groupeLignes, 'montant'),
      reste: _.sumBy(groupeLignes, (l) => l.reste || 0),
      quantite: quantiteRenseignee ? _.sumBy(groupeLignes, (l) => l.quantite || 0) : undefined,
    };
    lignesGroupees.push(representative);

    _.forEach(groupeLignes, (ligne) => {
      mapping[ligne.identifiant] = representative.identifiant;
    });
  });

  return { lignesGroupees, mapping };
};

/**
 * Remappe les identifiantLigneFacture des lignesRecu
 * pour pointer vers l'identifiant représentatif du groupe.
 */
export const remapLignesRecu = (
  lignesRecu: LigneRecuType[],
  mapping: Record<number, number>,
): LigneRecuType[] =>
  _.map(lignesRecu, (ligne) => ({
    ...ligne,
    identifiantLigneFacture:
      mapping[ligne.identifiantLigneFacture] ?? ligne.identifiantLigneFacture,
  }));
